import * as React from "react"

import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/shadcn/components/ui/dialog"
import DestinationOverride from "@/shadcn/components/settings/destination-override"

/**
 * Puts `destination-override` in a real dialog and does nothing else.
 *
 * The surface is deliberately not a portaling `<Dialog>` of its own — it draws
 * a card and stays reviewable on its own — so this is the seam the handoff
 * asks for. Same shape as `ConnectSocialDialog`, minus its complications:
 * that one runs `modal={false}` and has to put back the overlay and the
 * outside-click handling Radix omits; this one is modal, so Radix's own
 * overlay, focus containment, Escape and outside-click all work.
 *
 * `DialogPortal` already wraps its children in `.tw-root`, which is the portal
 * patch this repo needs — without it the card would render outside the scoped
 * preflight and lose its styling.
 */
export default function DestinationOverrideDialog({
  onClose,
  contentClassName,
  ...props
}: React.ComponentProps<typeof DestinationOverride> & {
  contentClassName?: string
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose?.()
      }}
    >
      {/*
        * `DialogContent` carries its own surface — bg-popover, p-4, rounded-xl,
        * a ring, sm:max-w-sm — and the override IS the surface here, so all of
        * it is switched off rather than painted twice. What is left is what the
        * primitive is wanted for: the portal, the overlay, focus containment
        * and the dismiss handlers.
        *
        * The card's own 640px cap stays with the card, so the box is released
        * at `sm` and the card centres inside it.
        */}
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className={cn(
          "tw:gap-0 tw:rounded-none tw:bg-transparent tw:p-0 tw:ring-0 tw:shadow-none tw:sm:max-w-none",
          contentClassName
        )}
      >
        {/* Radix requires a title for the accessible name. The card draws its
            own heading, so this is the same words, unseen. */}
        <DialogTitle className="tw:sr-only">
          {props.title ?? `Settings for ${props.accountName ?? "this output"}`}
        </DialogTitle>
        <DestinationOverride onClose={onClose} {...props} />
      </DialogContent>
    </Dialog>
  )
}
